import { useState, useRef, useEffect } from 'react'
import { ChevronDown, User as UserIcon, Settings, LogOut, Shield, BookOpen, Plus, FileText, Target } from 'lucide-react'

interface UserMenuUser {
  name: string
  email?: string
  avatarUrl?: string
  initials?: string
}

interface UserMenuProps {
  user: UserMenuUser
  currentLeagueName?: string
  isSuperAdmin?: boolean
  onProfile?: () => void
  onLeagueSettings?: () => void
  onRules?: () => void
  onCreateLeague?: () => void
  onMission?: () => void
  onTerms?: () => void
  onAdmin?: () => void
  onLogout?: () => void
}

export function UserMenu({
  user,
  currentLeagueName,
  isSuperAdmin,
  onProfile,
  onLeagueSettings,
  onRules,
  onCreateLeague,
  onMission,
  onTerms,
  onAdmin,
  onLogout,
}: UserMenuProps) {
  const [isOpen, setIsOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const initials = user.initials || user.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()

  return (
    <div className="relative shrink-0" ref={menuRef}>
      {/* Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 p-1.5 -m-1.5 rounded-lg transition-colors
                   hover:bg-zinc-100 dark:hover:bg-zinc-800"
      >
        {user.avatarUrl ? (
          <img
            src={user.avatarUrl}
            alt={user.name}
            className="w-8 h-8 rounded-full object-cover"
          />
        ) : (
          <div className="w-8 h-8 rounded-full bg-emerald-100 dark:bg-emerald-900 flex items-center justify-center">
            <span className="text-sm font-medium text-emerald-700 dark:text-emerald-300">
              {initials}
            </span>
          </div>
        )}
        <span className="hidden sm:inline text-sm font-medium text-zinc-900 dark:text-zinc-100">
          {user.name}
        </span>
        <ChevronDown className={`hidden sm:inline-block w-4 h-4 text-zinc-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-64 bg-white dark:bg-zinc-900 rounded-lg shadow-lg border border-zinc-200 dark:border-zinc-700 py-1 z-50">
          {/* User Info */}
          <div className="px-4 py-3 border-b border-zinc-100 dark:border-zinc-800">
            <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
              {user.name}
            </p>
            {user.email && (
              <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">
                {user.email}
              </p>
            )}
          </div>

          {/* Current League Label */}
          {currentLeagueName && (
            <div className="px-4 py-2 border-b border-zinc-100 dark:border-zinc-800">
              <p className="text-xs text-zinc-500 dark:text-zinc-400">
                League: <span className="font-medium text-zinc-700 dark:text-zinc-300">{currentLeagueName}</span>
              </p>
            </div>
          )}

          {/* Menu Items */}
          <div className="py-1">
            <button
              onClick={() => {
                onProfile?.()
                setIsOpen(false)
              }}
              className="w-full flex items-center gap-3 px-4 py-2 text-sm text-zinc-700 dark:text-zinc-300 transition-colors
                         hover:bg-zinc-50 dark:hover:bg-zinc-800"
            >
              <UserIcon className="w-4 h-4" />
              <span>Profile</span>
            </button>
            <button
              onClick={() => {
                onLeagueSettings?.()
                setIsOpen(false)
              }}
              className="w-full flex items-center gap-3 px-4 py-2 text-sm text-zinc-700 dark:text-zinc-300 transition-colors
                         hover:bg-zinc-50 dark:hover:bg-zinc-800"
            >
              <Settings className="w-4 h-4" />
              <span>League Settings</span>
            </button>
            <button
              onClick={() => {
                onRules?.()
                setIsOpen(false)
              }}
              className="w-full flex items-center gap-3 px-4 py-2 text-sm text-zinc-700 dark:text-zinc-300 transition-colors
                         hover:bg-zinc-50 dark:hover:bg-zinc-800"
            >
              <BookOpen className="w-4 h-4" />
              <span>Rules</span>
            </button>
            <button
              onClick={() => {
                onCreateLeague?.()
                setIsOpen(false)
              }}
              className="w-full flex items-center gap-3 px-4 py-2 text-sm text-zinc-700 dark:text-zinc-300 transition-colors
                          hover:bg-zinc-50 dark:hover:bg-zinc-800"
            >
              <Plus className="w-4 h-4" />
              <span>Create a League</span>
            </button>
            <button
              onClick={() => {
                onMission?.()
                setIsOpen(false)
              }}
              className="w-full flex items-center gap-3 px-4 py-2 text-sm text-zinc-700 dark:text-zinc-300 transition-colors
                         hover:bg-zinc-50 dark:hover:bg-zinc-800"
            >
              <Target className="w-4 h-4" />
              <span>Mission</span>
            </button>
            <button
              onClick={() => {
                onTerms?.()
                setIsOpen(false)
              }}
              className="w-full flex items-center gap-3 px-4 py-2 text-sm text-zinc-700 dark:text-zinc-300 transition-colors
                         hover:bg-zinc-50 dark:hover:bg-zinc-800"
            >
              <FileText className="w-4 h-4" />
              <span>Terms of Use</span>
            </button>
            {isSuperAdmin && (
              <button
                onClick={() => {
                  onAdmin?.()
                  setIsOpen(false)
                }}
                className="w-full flex items-center gap-3 px-4 py-2 text-sm text-zinc-700 dark:text-zinc-300 transition-colors
                           hover:bg-zinc-50 dark:hover:bg-zinc-800"
              >
                <Shield className="w-4 h-4" />
                <span>SuperAdmin Tools</span>
              </button>
            )}
          </div>

          {/* Sign Out */}
          <div className="border-t border-zinc-100 dark:border-zinc-800 py-1">
            <button
              onClick={() => {
                onLogout?.()
                setIsOpen(false)
              }}
              className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-600 dark:text-red-400 transition-colors
                         hover:bg-zinc-50 dark:hover:bg-zinc-800"
            >
              <LogOut className="w-4 h-4" />
              <span>Sign out</span>
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
